import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import logger from './logger';

// Store root references for each namespace to enable proper unmounting
const appRoots = new Map();

import {
  getStore,
  injectAsyncReducer,
  injectAsyncSagas,
  removeAsyncReducer,
  removeAsyncSagas,
} from '../../common/redux/store-utils';

function registerState(app) {
  // async add reducers
  if (app.reducers) {
    injectAsyncReducer(getStore(), app.namespace, app.reducers);
  }

  // async add sagas
  if (app.sagas) {
    injectAsyncSagas(app.sagas);
  }
}

function deregisterState({ namespace, sagas }) {
  //TODO explore alternatives, currently putting on a delay so rerenders don't occur after this has unmounted leading to state reads from state that is no longer there
  setTimeout(() => {
    removeAsyncReducer(namespace);

    if (sagas) {
      removeAsyncSagas(sagas);
    }
  }, 2000);
}

const createReactApp = ({ namespace, sagas, reducer, component, mountTo }) => {
  // this element is added to the page in ngx.core.js
  logger.error('createReactApp: You must provide a "mountTo" attribute', !mountTo);

  const _mountToNode = document.getElementById(mountTo);

  logger.error(
    'createReactApp: The node you are trying to mount your app to does not exist in the current DOM',
    !_mountToNode
  );
  logger.error('createReactApp: You must provide a "namespace" attribute', !namespace);
  logger.warn('createReactApp: You have not provided a "component"', !component);

  const store = getStore();
  const appMountNodeId = _createMountNodeId(namespace);

  // reducer must come first
  if (reducer) injectAsyncReducer(store, namespace, reducer);
  if (sagas) injectAsyncSagas(sagas);

  const mountNode = document.createElement('div');
  mountNode.setAttribute('id', appMountNodeId);

  _mountToNode.appendChild(mountNode);

  // Create root and store it for later unmounting
  const root = createRoot(document.getElementById(appMountNodeId));
  const uniqueKey = `${namespace}:${appMountNodeId}`;
  appRoots.set(uniqueKey, root);
  root.render(<Provider store={store}>{component}</Provider>);
};

/**
 * Will destroy the react app and remove the element from the DOM.
 * Due to react 18 parallel rendering, we might have to account for the fact, that the element is actually removed from the DOM before the app is destroyed!
 */
const destroyReactApp = ({ namespace, sagas }) => {
  logger.error('destroyReactApp: You must provide a "namespace" attribute', !namespace);

  const appMountNodeId = _createMountNodeId(namespace);
  const uniqueKey = `${namespace}:${appMountNodeId}`;

  // Use the stored root for unmounting
  const root = appRoots.get(uniqueKey);
  if (root) {
    setTimeout(() => {
      root.unmount();
    }, 0);
    appRoots.delete(uniqueKey);
  }
  const element = document.getElementById(appMountNodeId);
  if (element && element.parentNode) {
    element.parentNode.removeChild(element);
  }
};

function _createMountNodeId(namespace) {
  return `${namespace}-app`;
}

export default { registerState, createReactApp };
export { registerState, createReactApp, deregisterState, destroyReactApp };
